import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useTransactionsList } from './hooks'
import { Client, Gateway, Manager, Transaction } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Select, Table, Tag, Tooltip } from 'antd'
import { path, role } from '../../../../libs'
import {
  colorTransactionRequestStatus,
  currencyFilterList,
  humanTransactionRequestStatus,
  transactionRequestDirection,
  transactionRequestStatus,
} from '../../const'
import { ColumnsType } from 'antd/lib/table'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { FilterValue } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import { getNumberFormat } from '../../../../libs/utils'
import Title from 'antd/lib/typography/Title'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../../../libs/tableFunctions'
import { Deposit } from '../../deposits/list/types'
import { Guard } from '../../../../features/session/guard'

export const TransactionList = () => {
  const {
    transactions,
    totalCountOfTransactions,
    loading,
    onChangeTransactionsList,
  } = useTransactionsList()

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({
    // @ts-ignore
    createdAt: [dayjs().add(-7, 'd'), dayjs()],
  })
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['TRANSACTION_REQUEST_STATUS_ACCEPTED'],
  })

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    onChangeTransactionsList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const csvContent = transactions
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  const columns: ColumnsType<Transaction> = [
    {
      title: 'ID',
      dataIndex: 'transactionId',
      ...getColumnSearchProps<Transaction>(
        'transactionId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.transactionId) - parseInt(b.transactionId),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Client',
      children: [
        {
          title: 'ID',
          dataIndex: 'client',
          key: 'clientId',
          ...getColumnSearchProps<Transaction>(
            'clientId',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Client) => (
            <div>
              {item?.isTest ? (
                <Badge count={'Test'} offset={[0, -10]}>
                  <a href={path.clients.details(item.clientId)} target="_blank">
                    {item.clientId}
                  </a>
                </Badge>
              ) : (
                <a href={path.clients.details(item.clientId)} target="_blank">
                  {item.clientId}
                </a>
              )}
              {item.isPremium ? (
                <Tooltip title="Premium">{'\u00A0👑'}</Tooltip>
              ) : (
                ''
              )}
            </div>
          ),
        },
        {
          title: 'Full name',
          dataIndex: 'client',
          key: 'client.fullName',
          ...getColumnSearchProps<Transaction>(
            'client.fullName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Client) => (
            <a
              href={path.clients.details(item.clientId)}
              target="_blank"
              style={{
                textDecoration: 'none',
                color: 'black',
              }}
            >
              <div>
                {item.fullName}
                <div
                  style={{
                    color: '#2eb85c',
                  }}
                >
                  {item.partnerStatus ? ` IB - ${item.partnerStatus}` : ''}
                </div>
              </div>
            </a>
          ),
        },
        {
          title: 'Country',
          dataIndex: 'client',
          key: 'client.country',
          render: (item: Client) => {
            return <div>{item.country}</div>
          },
        },
      ],
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      ...getColumnSearchDateProps<Transaction>(
        'createdAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      ...getColumnSearchDateProps<Transaction>(
        'updatedAt',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
    },
    {
      title: 'Payment system',
      children: [
        {
          title: 'Provider',
          ...getColumnSearchProps<Transaction>(
            'processingProvider',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Transaction) => (
            <>
              {item?.processingProvider}
              <br />
              {item?.externalTransferId}
            </>
          ),
        },
        {
          title: 'Method',
          dataIndex: 'gateway',
          ...getColumnSearchProps<Transaction>(
            'gateway.internalName',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (item: Gateway) => <>{item?.internalName}</>,
        },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      defaultFilteredValue: ['TRANSACTION_REQUEST_STATUS_ACCEPTED'],
      render: (item: string) => (
        <>
          <Tag color={colorTransactionRequestStatus(item)}>
            {humanTransactionRequestStatus(item)}
          </Tag>
        </>
      ),
      filters: [
        {
          text: humanTransactionRequestStatus(transactionRequestStatus.new()),
          value: transactionRequestStatus.new(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.pendingManager(),
          ),
          value: transactionRequestStatus.pendingManager(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.pendingDestination(),
          ),
          value: transactionRequestStatus.pendingDestination(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.accepted(),
          ),
          value: transactionRequestStatus.accepted(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.rejected(),
          ),
          value: transactionRequestStatus.rejected(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.pendingRollback(),
          ),
          value: transactionRequestStatus.pendingRollback(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.questions(),
          ),
          value: transactionRequestStatus.questions(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.missingInformation(),
          ),
          value: transactionRequestStatus.missingInformation(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.fundsReserved(),
          ),
          value: transactionRequestStatus.fundsReserved(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.unconfirmed(),
          ),
          value: transactionRequestStatus.unconfirmed(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.vendorError(),
          ),
          value: transactionRequestStatus.vendorError(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.internalError(),
          ),
          value: transactionRequestStatus.internalError(),
        },
        {
          text: humanTransactionRequestStatus(
            transactionRequestStatus.dealersCheck(),
          ),
          value: transactionRequestStatus.dealersCheck(),
        },
      ],
      filterSearch: true,
      onFilter: (value: any, record) => record.status == value,
    },
    {
      title: 'TID',
      ...getColumnSearchProps<Transaction>(
        'externalTransaction.externalId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: Deposit) => (
        <>
          {item?.externalTransaction?.externalId}
          {item?.externalPaymentTransaction?.externalId}
        </>
      ),
    },
    {
      title: 'Initial amount',
      children: [
        {
          title: 'Amount',
          ...getColumnSearchProps<Transaction>(
            'initialAmount',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (transaction: Transaction) => {
            return (
              <>
                {transaction.direction == transactionRequestDirection.In() ? (
                  <Tag color="success">
                    {getNumberFormat(transaction.initialAmount, 2)}
                  </Tag>
                ) : (
                  <Tag color="error">
                    {`-${getNumberFormat(transaction.initialAmount, 2)}`}
                  </Tag>
                )}
              </>
            )
          },
        },
        {
          title: 'currency',
          dataIndex: 'initialCurrency',
          filters: currencyFilterList,
          filterSearch: true,
          render: (initialCurrency: string) => <>{initialCurrency}</>,
          onFilter: (value: any, record) => record.initialCurrency == value,
        },
      ],
    },
    {
      title: 'Client amount',
      children: [
        {
          title: 'Amount',
          ...getColumnSearchProps<Transaction>(
            'clientAmount',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (transaction: Transaction) => (
            <>
              {transaction.direction == transactionRequestDirection.In() ? (
                <Tag color="success">
                  {getNumberFormat(transaction.clientAmount, 2)}
                </Tag>
              ) : (
                <Tag color="error">
                  {`-${getNumberFormat(transaction.clientAmount, 2)}`}
                </Tag>
              )}
            </>
          ),
        },
        {
          title: 'Currency',
          dataIndex: 'clientCurrency',
          filters: currencyFilterList,
          filterSearch: true,
          render: (clientCurrency: string) => <>{clientCurrency}</>,
          onFilter: (value: any, record) => record.clientCurrency == value,
        },
      ],
    },
    {
      title: 'CompanyAmount',
      children: [
        {
          title: 'Amount',
          ...getColumnSearchProps<Transaction>(
            'companyAmount',
            setSearchText,
            searchText,
            searchInput,
          ),
          render: (transaction: Transaction) => (
            <>
              {transaction.direction == transactionRequestDirection.In() ? (
                <Tag color="success">
                  {getNumberFormat(transaction.companyAmount, 2)}
                </Tag>
              ) : (
                <Tag color="error">
                  {`-${getNumberFormat(transaction.companyAmount, 2)}`}
                </Tag>
              )}
            </>
          ),
        },
        {
          title: 'Currency',
          dataIndex: 'companyCurrency',
          filters: currencyFilterList,
          filterSearch: true,
          render: (companyCurrency: string) => <>{companyCurrency}</>,
          onFilter: (value: any, record) => record.companyCurrency == value,
        },
      ],
    },
    {
      title: 'Product',
      dataIndex: 'product',
      filters: [
        {
          text: 'Headway',
          value: 'headway',
        },
        {
          text: 'Nova',
          value: 'nova',
        },
      ],
    },
    // {
    //   title: 'Commission',
    //   children: [
    //     {
    //       title: 'All',
    //       render: (item: Transaction) => (
    //         <>
    //           {Number(item.clientCommission) + Number(item.companyCommission)}{' '}
    //           {item.commissionCurrency}
    //         </>
    //       ),
    //     },
    //     {
    //       title: 'Company commission',
    //       render: (item: Transaction) => (
    //         <>
    //           {item.companyCommission} {item.commissionCurrency}
    //         </>
    //       ),
    //     },
    //     {
    //       title: 'Client commission',
    //       render: (item: Transaction) => (
    //         <>
    //           {item.clientCommission} {item.commissionCurrency}
    //         </>
    //       ),
    //     },
    //   ],
    // },
    {
      title: 'Rate',
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      render: (exchangeRate: string) => {
        return <div>{getNumberFormat(exchangeRate, 6)}</div>
      },
    },
    {
      title: 'Manager',
      dataIndex: 'changeHistory',
      key: 'changeHistory.user',
      render: (item: Manager) => (
        <>
          <div>{item?.user?.email}</div>
        </>
      ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={
                item.direction == transactionRequestDirection.In()
                  ? path.billing.deposit.edit(item.transactionId)
                  : path.billing.withdrawal.edit(item.transactionId)
              }
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
  ]

  const handleChangeTypeSelect = (value: FilterValue) => {
    if (value) {
      setFiltersState((prevState) => ({
        ...prevState,
        direction: value,
      }))
    } else {
      setFiltersState((prevState) => {
        delete prevState['direction']
        return {
          ...prevState,
        }
      })
    }
  }

  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <Guard permission="export_transactions">
            <CButton
              color="primary"
              href={csvCode}
              download="coreui-table-data.csv"
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          </Guard>
        </div>
        <div>
          <Title level={5}>Type</Title>
          <Select
            // @ts-ignore
            defaultValue=""
            style={{ width: 250 }}
            onChange={(value: FilterValue) => handleChangeTypeSelect(value)}
            options={[
              { value: '', label: 'All' },
              { value: transactionRequestDirection.In(), label: 'Deposits' },
              {
                value: transactionRequestDirection.Out(),
                label: 'Withdrawal',
              },
            ]}
          />
        </div>
      </div>

      <Table
        dataSource={transactions}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="transactionId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          pageSize: pageSize,
          total: totalCountOfTransactions,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      />
    </CCardBody>
  )
}
